// https://codepen.io/brady_wright/pen/NNOvrW
import React from "react";
import { css } from "@emotion/react";

const timelineLayout = css`
  --primary-color: #03b670;
  --primary-color-hover: scale-color(var(--primary-color), $lightness: 32%);

  color: #768390;
  background: #fff;
  font-family: "Effra", Helvetica, sans-serif;
  padding: 0;
  -webkit-font-smoothing: antialiased;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: #3d4351;
    margin-top: 0;
  }
  a {
    color: var(--primary-color);
    &:hover {
      color: var(--primary-color-hover);
      text-decoration: none;
    }
  }
  .example-header {
    background: #3d4351;
    color: #fff;
    font-weight: 300;
    padding: 3em 1em;
    text-align: center;
    h1 {
      color: #fff;
      font-weight: 300;
      margin-bottom: 20px;
    }
    p {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 3px;
      font-weight: 700;
    }
  }
  .container-fluid {
    .row {
      //padding: 0 0 4em 0;
      &:nth-of-type(even) {
        background: #f1f4f5;
      }
    }
  }

  .example-title {
    text-align: center;
    margin-bottom: 60px;
    padding: 3em 0;
    border-bottom: 1px solid #e4eaec;
    p {
      margin: 0 auto;
      font-size: 16px;
      max-width: 400px;
    }
  }

  /*==================================
    TIMELINE
==================================*/

  /*-- GENERAL STYLES
    ------------------------------*/
  .timeline {
    line-height: 1.4em;
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      line-height: inherit;
    }
  }

  /*----- TIMELINE ITEM -----*/

  .timeline-item {
    padding-left: 40px;
    position: relative;
    &:last-child {
      padding-bottom: 0;
    }
  }

  /*----- TIMELINE INFO -----*/

  .timeline-info {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 3px;
    margin: 0 0 0.5em 0;
    text-transform: uppercase;
    white-space: nowrap;
  }
  /*----- TIMELINE MARKER -----*/

  .timeline-marker {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 15px;
    &:before {
      background: transparent;
      border: 3px solid var(--primary-color);
      border-radius: 100%;
      content: "";
      display: block;
      height: 15px;
      position: absolute;
      top: 4px;
      left: 0;
      width: 15px;
      transition: background 0.3s ease-in-out, border 0.3s ease-in-out;
    }
    &:after {
      content: "";
      width: 3px;
      background: #ccd5db;
      display: block;
      position: absolute;
      top: 24px;
      bottom: 0;
      left: 6px;
    }
    .timeline-item:last-child &:after {
      content: none;
    }
  }
  .timeline-item:not(.period):hover .timeline-marker:before {
    background: var(--primary-color);
    border: 3px solid transparent;
  }

  /*----- TIMELINE CONTENT -----*/

  .timeline-content {
    padding-bottom: 40px;
    p:last-child {
      margin-bottom: 0;
    }
  }

  /*----- TIMELINE PERIOD -----*/

  .period {
    padding: 0;
    .timeline-info {
      display: none;
    }
    .timeline-marker {
      &:before {
        background: transparent;
        content: "";
        width: 15px;
        height: auto;
        border: none;
        border-radius: 0;
        top: 0;
        bottom: 30px;
        position: absolute;
        border-top: 3px solid #ccd5db;
        border-bottom: 3px solid #ccd5db;
      }
      &:after {
        content: "";
        height: 32px;
        top: auto;
      }
    }
    .timeline-content {
      padding: 40px 0 70px;
    }
    .timeline-title {
      margin: 0;
      padding: 0;
      border: none;
    }
  }

  /*----------------------------------------------
        MOD: TIMELINE SPLIT
    ----------------------------------------------*/

  .timeline-split {
    @media (min-width: 45em) {
      .timeline {
        display: table;
      }
      .timeline-item {
        display: table-row;
        padding: 0;
      }
      .timeline-info,
      .timeline-marker,
      .timeline-content,
      .period .timeline-info {
        display: table-cell;
        vertical-align: top;
      }
      .timeline-marker {
        position: relative;
      }
      .timeline-content {
        padding-left: 30px;
      }
      .timeline-info {
        padding-right: 30px;
      }
      .period .timeline-title {
        position: relative;
        left: -45px;
      }
    }
  }

  /*----------------------------------------------
        MOD: TIMELINE CENTERED
    ----------------------------------------------*/

  .timeline-centered {
    @extend .timeline-split;
    @media (min-width: 992px) {
      &,
      .timeline-item,
      .timeline-info,
      .timeline-marker,
      .timeline-content {
        display: block;
        margin: 0;
        padding: 0;
      }
      .timeline-item {
        padding-bottom: 40px;
        overflow: hidden;
      }
      .timeline-marker {
        position: absolute;
        left: 50%;
        margin-left: -7.5px;
      }
      .timeline-info,
      .timeline-content {
        width: 50%;
      }
      > .timeline-item:nth-of-type(odd) .timeline-info {
        float: left;
        text-align: right;
        padding-right: 30px;
      }
      > .timeline-item:nth-of-type(odd) .timeline-content {
        float: right;
        text-align: left;
        padding-left: 30px;
      }
      > .timeline-item:nth-of-type(even) .timeline-info {
        float: right;
        text-align: left;
        padding-left: 30px;
      }
      > .timeline-item:nth-of-type(even) .timeline-content {
        float: left;
        text-align: right;
        padding-right: 30px;
      }
      > .timeline-item.period .timeline-content {
        float: none;
        padding: 0;
        width: 100%;
        text-align: center;
      }
      .timeline-item.period {
        padding: 50px 0 90px;
      }
      .period .timeline-marker:after {
        height: 30px;
        bottom: 0;
        top: auto;
      }
      .period .timeline-title {
        left: auto;
      }
    }
  }

  /*----------------------------------------------
        MOD: MARKER OUTLINE
    ----------------------------------------------*/

  .marker-outline {
    .timeline-marker {
      &:before {
        background: transparent;
        border-color: var(--primary-color);
      }
    }
    .timeline-item:hover .timeline-marker:before {
      background: var(--primary-color);
    }
  }

  .timelineAuthor {
    font-size: 0.5em;
  }
`;

//mode String: basic, split, centered
//items [JSON]: {date, title, content} | {timeTitle}

export function TimelineItem(props) {
  return props.title && props.date ? (
    <li className="timeline-item">
      <div className="timeline-info">
        <span>{props.date}</span>
      </div>
      <div className="timeline-marker"></div>
      <div className="timeline-content">
        <h3 className="timeline-title">{props.title}</h3>
        {props.children}
      </div>
    </li>
  ) : (
    <li className="timeline-item period">
      <div className="timeline-info"></div>
      <div className="timeline-marker"></div>
      <div className="timeline-content">
        <h2 className="timeline-title">{props.title}</h2>
      </div>
    </li>
  );
}

export function Timeline(props) {
  return (
    <div css={timelineLayout}>
      <div className="container-fluid">
        <div className={"row example-" + props.mode}></div>
        <div className="col-xs-10 col-xs-offset-1 col-sm-8 col-sm-offset-2">
          <ul className={"timeline timeline-" + props.mode}>
            {props.children}
          </ul>
        </div>
      </div>
      <span className="timelineAuthor">
        Timeline-Code basierend auf: Simple Responsive Timeline von Brady Wright
      </span>
    </div>
  );
}
